import { LucideIcon } from 'lucide-react';

import { TextRole } from '../../../../queries';
import { createId } from '../../model/factories';
import { AdElement, AdScene, ShapeElement, TextElement } from '../../model/types';

/**
 * Gotowe elementy kreacji: kilka kształtów i napisów wstawianych jednym
 * kliknięciem jako komplet.
 *
 * Różnica względem szablonów jest zasadnicza: szablon zastępuje CAŁĄ planszę,
 * a element dokłada się do tego, co już na niej jest. Dzięki temu da się złożyć
 * kreację z klocków, zamiast zaczynać za każdym razem od gotowego układu.
 *
 * Wymiary liczymy z krótszego boku planszy, więc ten sam element wygląda
 * proporcjonalnie tak samo na kwadracie i na relacji 9:16.
 */

export interface ComponentPalette {
  dark: string;
  mid: string;
  accent: string;
  light: string;
}

export interface ComponentContext {
  scene: AdScene;
  palette: ComponentPalette;
  fonts: Record<TextRole, string>;
  /** Sygnet marki (kwadratowy znak) z ustawień albo wbudowany znak Hotelspota. */
  logoUrl: string;
  /** Pełne logo z napisem. Bez własnego pliku w ustawieniach: logo Hotelspota. */
  wordmarkUrl: string;
}

export interface AdComponent {
  id: string;
  label: string;
  description: string;
  icon: LucideIcon;
  build: (context: ComponentContext) => AdElement[];
}

/**
 * Kolory elementu wyprowadzone z palety marki z ustawień. Pierwsze kolory to
 * tła i bloki, dalsze to akcenty — taką kolejność zakłada też prompt AI.
 */
export const toComponentPalette = (brandColors: string[]): ComponentPalette => ({
  dark: brandColors[0] ?? '#0f3333',
  mid: brandColors[1] ?? '#1a4d4d',
  accent: brandColors[3] ?? brandColors[2] ?? '#c18344',
  light: '#ffffff',
});

export const base = (name: string) => ({
  id: createId(),
  name,
  rotation: 0,
  opacity: 1,
  locked: false,
  hidden: false,
});

export const text = (
  name: string,
  config: Partial<TextElement> & Pick<TextElement, 'text' | 'x' | 'y' | 'width' | 'fontSize' | 'fontFamily'>,
): TextElement => ({
  ...base(name),
  type: 'text',
  height: config.fontSize * 1.3,
  fontWeight: 700,
  fontStyle: 'normal',
  color: '#ffffff',
  align: 'left',
  lineHeight: 1.25,
  letterSpacing: 0,
  uppercase: false,
  background: '',
  backgroundRadius: 0,
  padding: 0,
  stroke: '',
  strokeWidth: 0,
  autoFit: false,
  ...config,
});

export const shape = (
  name: string,
  config: Partial<ShapeElement> & Pick<ShapeElement, 'x' | 'y' | 'width' | 'height'>,
): ShapeElement => ({
  ...base(name),
  type: 'shape',
  shape: 'rect',
  fill: '#c18344',
  stroke: '',
  strokeWidth: 0,
  radius: 0,
  ...config,
});

/** Skala elementów liczona z krótszego boku planszy. */
export const unit = (scene: AdScene): number => Math.min(scene.width, scene.height);

/** Margines boczny, ten sam, którego trzymają się szablony. */
export const margin = (scene: AdScene): number => Math.round(scene.width * 0.08);
